// THE CONTENT LANGUAGE — the operator's second language knob, and the machinery behind `<Tx>`.
// CONTENT-TRANSLATION-ARCH §5.2–§5.3.
//
// Two languages live in this console and they are not the same question:
//   · the UI language (i18n/index.tsx) — what the LABELS and BUTTONS say;
//   · the CONTENT language (here) — what language a requirement statement, a deliverable's
//     definition of done or a client's comment is shown in.
// The default is 'original': the record as it was actually written, claiming nothing.
//
// What this file guarantees about cost: ONE resolve request per page, per language — not one per
// field, not one per row. Every `<Tx>` on a page registers its demand in an effect; the demands
// that land in the same commit are flushed together on the next tick. A page with 200 fields
// makes one request.
//
// What it guarantees about honesty: the store only ever holds what the SERVER said about a field.
// It never guesses that a field is translated, never caches across languages, and never lets a
// per-item flip write back to the server — a flip is this browser's view of one row, nothing more.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  useSyncExternalStore, type ReactNode,
} from 'react';

import { request } from '../api/client.js';
import { translationsApi, type ResolveItem } from '../api/translations.js';
import { useEngagement } from '../context/EngagementContext.js';
import { useLocale } from './index.js';
import type { TranslationLang } from '../../shared/language.js';
import {
  entityKey, fieldKey, isContentLangPref,
  type ContentEntityType, type ContentLangPref, type OriginalInfo, type ResolveEntry,
} from '../../shared/contentTranslation.js';

const PREF_KEY = 'signet.contentLang';
const BADGE_KEY = 'signet.tx.badges';
const FLIP_KEY = 'signet.tx.flips';
/** Per-item flips are a browser convenience, deliberately not server state (§5.3). Capped so the
 *  key cannot grow without bound; oldest flips fall off first. */
const FLIP_CAP = 500;
/** How long before a page re-asks about fields the server said were still being translated. */
const POLL_MS = 20_000;
/** …and how many times, so a page left open against a dead model does not poll forever. */
const MAX_POLLS = 30;

const readLocal = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeLocal = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* private mode */ }
};

// ---------------------------------------------------------------------------------------------
// The batching store — one per (engagement, language).
// ---------------------------------------------------------------------------------------------

/** Keys are stable strings; entries are stable object references until the server replaces them,
 *  which is what lets every `<Tx>` read through `useSyncExternalStore` without re-render storms. */
class TxStore {
  private texts = new Map<string, ResolveEntry>();
  private originals = new Map<string, OriginalInfo>();
  private wanted = new Map<string, ResolveItem>();
  private asked = new Set<string>();
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private polls = 0;
  private version = 0;
  private disposed = false;

  constructor(private engagementId: number, private lang: TranslationLang) {}

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  };

  getVersion = (): number => this.version;

  entry(key: string): ResolveEntry | undefined { return this.texts.get(key); }
  original(key: string): OriginalInfo | undefined { return this.originals.get(key); }

  /** Register one field. Idempotent — a field already asked about is not asked again unless a
   *  poll deliberately re-arms it. */
  demand(entityType: ContentEntityType, entityId: number, field: string): void {
    const key = fieldKey(entityType, entityId, field);
    if (this.asked.has(key) || this.texts.has(key)) return;
    const ekey = entityKey(entityType, entityId);
    const item = this.wanted.get(ekey);
    if (item) {
      if (!item.fields.includes(field)) item.fields.push(field);
    } else {
      this.wanted.set(ekey, { entity_type: entityType, entity_id: entityId, fields: [field] });
    }
    this.schedule();
  }

  private schedule(): void {
    if (this.timer || this.disposed) return;
    // Zero-delay, not a microtask: React runs every mounted `<Tx>`'s effect in one commit, so a
    // timeout scheduled from the first of them still collects the last.
    this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, 0);
  }

  private bump(): void {
    this.version += 1;
    for (const fn of this.listeners) fn();
  }

  private async flush(): Promise<void> {
    if (this.disposed) return;
    const items = [...this.wanted.values()];
    if (!items.length) return;
    this.wanted.clear();
    for (const it of items) for (const f of it.fields) this.asked.add(fieldKey(it.entity_type, it.entity_id, f));

    try {
      const answer = await translationsApi.resolve({ engagement_id: this.engagementId, lang: this.lang, items });
      if (this.disposed) return;
      for (const [k, v] of Object.entries(answer.texts)) this.texts.set(k, v);
      for (const [k, v] of Object.entries(answer.originals)) this.originals.set(k, v);
      this.bump();
      if (answer.pending.length) this.armPoll(answer.pending);
    } catch {
      // A failed resolve leaves every `<Tx>` rendering its original, unmarked — which is the
      // correct thing to show when we cannot say anything about a translation. Re-arm so the
      // next demand (a navigation, a poll) tries again.
      for (const it of items) for (const f of it.fields) this.asked.delete(fieldKey(it.entity_type, it.entity_id, f));
    }
  }

  /** Fields the server owes but has not written yet: ask again shortly, the same way the
   *  proposals rail waits for descriptors to land. Bounded, so a page left open against a dead
   *  model stops asking. */
  private armPoll(pending: string[]): void {
    if (this.pollTimer || this.disposed || this.polls >= MAX_POLLS) return;
    this.polls += 1;
    this.pollTimer = setTimeout(() => {
      this.pollTimer = null;
      if (this.disposed) return;
      for (const key of pending) {
        const [entityType, id, field] = key.split(':');
        this.asked.delete(key);
        this.texts.delete(key);
        this.demand(entityType as ContentEntityType, Number(id), field);
      }
    }, POLL_MS);
  }

  /** An operator edit or revert answered authoritatively — write it straight in rather than
   *  round-tripping a whole page's resolve. */
  put(key: string, entry: ResolveEntry): void {
    this.texts.set(key, entry);
    this.asked.add(key);
    this.bump();
  }

  /** After a revert, the field is owed a machine translation again — forget it so the next paint
   *  asks, and start the poll that waits for it. */
  forget(key: string): void {
    this.texts.delete(key);
    this.asked.delete(key);
    this.bump();
    this.armPoll([key]);
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.pollTimer) clearTimeout(this.pollTimer);
    this.listeners.clear();
  }
}

// ---------------------------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------------------------

export interface ContentLangCtx {
  /** The stored preference, including the two indirect values. */
  pref: ContentLangPref;
  setPref: (p: ContentLangPref) => void;
  /** The language to READ in — null means "the record as authored", which is what 'original'
   *  resolves to and what an unsupported UI language would fall back to. */
  lang: TranslationLang | null;
  /** Show the original-language chip on EVERY item, not only on the ones you are reading in
   *  translation. The owner asked to be able to see, at a glance, what language each item was
   *  written in; this is that switch. Browser-local — it changes nothing about the record. */
  showBadges: boolean;
  setShowBadges: (v: boolean) => void;
  /** Per-item override: `${entity_type}:${entity_id}` → 'original' | a language. */
  flips: Record<string, string>;
  flip: (entityType: string, entityId: number, to: string | null) => void;
  storeFor: (engagementId: number, lang: TranslationLang) => TxStore;
}

const Ctx = createContext<ContentLangCtx>({
  pref: 'original', setPref: () => {}, lang: null,
  showBadges: false, setShowBadges: () => {},
  flips: {}, flip: () => {},
  storeFor: () => { throw new Error('ContentLangProvider is missing'); },
});

function initialPref(): ContentLangPref {
  const stored = readLocal(PREF_KEY);
  return isContentLangPref(stored) ? stored : 'original';
}

/**
 * Mounted inside EngagementProvider (it needs the current engagement) and inside LocaleProvider
 * (pref 'ui' follows the console language). Reads the stored preference once the operator is
 * known; the localStorage copy keeps the first paint right, exactly like LocaleProvider does.
 */
export function ContentLangProvider({ children }: { children: ReactNode }) {
  const { lang: uiLang } = useLocale();
  const [pref, setPrefState] = useState<ContentLangPref>(initialPref);
  const [showBadges, setShowBadgesState] = useState<boolean>(() => readLocal(BADGE_KEY) === '1');
  const [flips, setFlips] = useState<Record<string, string>>(() => {
    try { return JSON.parse(readLocal(FLIP_KEY) ?? '{}') as Record<string, string>; } catch { return {}; }
  });

  useEffect(() => {
    request<{ content_language: string }>('/me/prefs')
      .then((p) => { if (isContentLangPref(p.content_language)) setPrefState(p.content_language); })
      .catch(() => null);
  }, []);

  const setPref = useCallback((p: ContentLangPref) => {
    setPrefState(p);
    writeLocal(PREF_KEY, p);
    void request('/me/prefs', { method: 'PUT', body: JSON.stringify({ content_language: p }) }).catch(() => null);
  }, []);

  const setShowBadges = useCallback((v: boolean) => {
    setShowBadgesState(v);
    writeLocal(BADGE_KEY, v ? '1' : '0');
  }, []);

  const flip = useCallback((entityType: string, entityId: number, to: string | null) => {
    setFlips((prev) => {
      const key = entityKey(entityType, entityId);
      const next = { ...prev };
      if (to === null) delete next[key];
      else next[key] = to;
      const keys = Object.keys(next);
      if (keys.length > FLIP_CAP) for (const k of keys.slice(0, keys.length - FLIP_CAP)) delete next[k];
      writeLocal(FLIP_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  // 'ui' follows the console. The console's languages (en/de/fr) are all content targets, so this
  // never has to fall back — but if that ever changes, an unknown one reads as 'original', which
  // shows the record rather than inventing a language to show it in.
  const lang: TranslationLang | null = useMemo(() => {
    if (pref === 'original') return null;
    const wanted = pref === 'ui' ? uiLang : pref;
    return wanted === 'de' || wanted === 'en' || wanted === 'fr' ? wanted : null;
  }, [pref, uiLang]);

  // One store per (engagement, language); switching either starts a clean one rather than
  // reusing a cache that answers a different question.
  const stores = useRef(new Map<string, TxStore>());
  useEffect(() => () => { for (const s of stores.current.values()) s.dispose(); stores.current.clear(); }, []);
  const storeFor = useCallback((engagementId: number, l: TranslationLang): TxStore => {
    const key = `${engagementId}|${l}`;
    let store = stores.current.get(key);
    if (!store) {
      // Keep the map small: a console visits one or two engagements in a sitting.
      if (stores.current.size >= 4) {
        const oldest = stores.current.keys().next().value as string | undefined;
        if (oldest) { stores.current.get(oldest)?.dispose(); stores.current.delete(oldest); }
      }
      store = new TxStore(engagementId, l);
      stores.current.set(key, store);
    }
    return store;
  }, []);

  const value = useMemo<ContentLangCtx>(
    () => ({ pref, setPref, lang, showBadges, setShowBadges, flips, flip, storeFor }),
    [pref, setPref, lang, showBadges, setShowBadges, flips, flip, storeFor],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useContentLang = (): ContentLangCtx => useContext(Ctx);

// ---------------------------------------------------------------------------------------------
// The hook every field goes through
// ---------------------------------------------------------------------------------------------

export interface TxState {
  /** What to render: the record, or a translation of it. */
  showing: 'original' | 'translation';
  /** The translation, when `showing === 'translation'`. */
  text: string | null;
  /** The language the reader is currently looking at. */
  lang: TranslationLang | null;
  /** What language the entity was WRITTEN in — the thing the badge names. */
  originalLang: string | null;
  /** How that original language was decided; 'default_correspondence' means it was assumed. */
  detector: string | null;
  provenance: 'machine' | 'operator' | null;
  model: string | null;
  stale: boolean;
  flags: ResolveEntry['flags'];
  frozen: boolean;
  /** A translation is owed and has not landed yet — render the original, say "translating". */
  pending: boolean;
  /** This one item has been flipped away from the page's language. */
  flipped: boolean;
  /** Flip this item to a language, or back to the record. Browser-local. */
  setItemLang: (to: string | null) => void;
  /** Write an operator correction / revert to machine, and update this page in place. */
  saveCorrection: (text: string) => Promise<void>;
  revertToMachine: () => Promise<void>;
  /** The engagement this field belongs to, when one is in scope. */
  engagementId: number | null;
}

/**
 * Resolve ONE field. Registers its demand with the page's store on mount; the store batches.
 *
 * `engagementId` defaults to the console's current engagement. A surface with no engagement in
 * scope (a client-level rail, say) resolves nothing and renders the record — which is right:
 * without an engagement there is no term base and no queue to ask.
 */
export function useTx(
  entityType: ContentEntityType,
  entityId: number | null | undefined,
  field: string,
  engagementIdOverride?: number | null,
): TxState {
  const { lang, flips, flip, storeFor } = useContentLang();
  const { current } = useEngagement();
  const engagementId = engagementIdOverride ?? current?.id ?? null;

  const itemFlip = entityId ? flips[entityKey(entityType, entityId)] : undefined;
  // The flip decides this item's language; the page's setting decides everything else's.
  const wanted: TranslationLang | null = itemFlip === 'original'
    ? null
    : (itemFlip === 'de' || itemFlip === 'en' || itemFlip === 'fr' ? itemFlip : lang);

  const store = engagementId && wanted ? storeFor(engagementId, wanted) : null;
  const key = entityId ? fieldKey(entityType, entityId, field) : '';
  const ekey = entityId ? entityKey(entityType, entityId) : '';

  // Subscribing keeps this field live; demanding is a side effect and belongs in an effect.
  const noop = useCallback(() => () => {}, []);
  const zero = useCallback(() => 0, []);
  useSyncExternalStore(store ? store.subscribe : noop, store ? store.getVersion : zero, store ? store.getVersion : zero);
  useEffect(() => {
    if (store && entityId) store.demand(entityType, entityId, field);
  }, [store, entityType, entityId, field]);

  const entry = store && key ? store.entry(key) : undefined;
  const originalInfo = store && ekey ? store.original(ekey) : undefined;

  const setItemLang = useCallback((to: string | null) => {
    if (entityId) flip(entityType, entityId, to);
  }, [flip, entityType, entityId]);

  const saveCorrection = useCallback(async (text: string) => {
    if (!entityId || !wanted || !store) return;
    const saved = await translationsApi.put(entityType, entityId, field, wanted, text);
    store.put(key, saved);
  }, [entityId, wanted, store, entityType, field, key]);

  const revertToMachine = useCallback(async () => {
    if (!entityId || !wanted || !store) return;
    await translationsApi.revert(entityType, entityId, field, wanted);
    store.forget(key);
  }, [entityId, wanted, store, entityType, field, key]);

  const showTranslation = entry?.status === 'translated' && typeof entry.text === 'string';
  return {
    showing: showTranslation ? 'translation' : 'original',
    text: showTranslation ? entry!.text : null,
    lang: wanted,
    originalLang: originalInfo?.lang ?? null,
    detector: originalInfo?.detector ?? null,
    provenance: entry?.provenance ?? null,
    model: entry?.model ?? null,
    stale: entry?.stale ?? false,
    flags: entry?.flags ?? null,
    frozen: entry?.frozen === true,
    pending: entry?.status === 'pending',
    flipped: itemFlip !== undefined,
    setItemLang,
    saveCorrection,
    revertToMachine,
    engagementId,
  };
}
